"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WsAiHubAdminPersonas = void 0;
var react_1 = __importStar(require("react"));
var react_router_dom_1 = require("react-router-dom");
var office_ui_fabric_react_1 = require("office-ui-fabric-react");
var components_1 = require("../shared/components");
var ai_hub_route_utils_1 = require("../shared/utils/ai-hub-route-utils");
var constants_1 = require("./constants");
var PersonasCategoriesPanel_1 = require("./components/PersonasCategoriesPanel");
var PersonasPropertiesPanel_1 = require("./components/PersonasPropertiesPanel");
var PersonasSaveControls_1 = require("./components/PersonasSaveControls");
var PersonasToolbar_1 = require("./components/PersonasToolbar");
var use_personas_draft_schema_1 = require("./hooks/use-personas-draft-schema");
var use_personas_search_1 = require("./hooks/use-personas-search");
var personas_mock_data_1 = require("./mock/personas-mock-data");
var personas_utils_1 = require("./utils/personas-utils");
require("./styles/personas.scss");
/**
 * WsAiHubAdminPersonas - User personas management for AI Hub admin.
 *
 * Thin orchestrator that wires together:
 *  - {@link usePersonasDraftSchema}: saved/draft state + change tracking
 *  - {@link usePersonasSearch}: derived search analysis
 *  - Local UI state: selected group, selected personas, inline group
 *    edit, success message, delete confirmation, callout.
 *
 * Mirrors the Metrics admin tab — categorized read-only items grouped
 * into editable buckets. Here, items are personas extracted from the
 * use-case Markdown and groups represent business functions / persona
 * segments curated by the admin.
 *
 * @returns {JSX.Element} Rendered Personas admin view.
 */
var WsAiHubAdminPersonas = function () {
    var history = react_router_dom_1.useHistory();
    var draft = use_personas_draft_schema_1.usePersonasDraftSchema(personas_mock_data_1.PERSONAS_MOCK_SCHEMA);
    var draftSchema = draft.draftSchema, savedSchema = draft.savedSchema, hasChanges = draft.hasChanges, updateDraft = draft.updateDraft, saveChanges = draft.saveChanges, discardChanges = draft.discardChanges, isCategoryChanged = draft.isCategoryChanged, isPropertyChanged = draft.isPropertyChanged, findCategory = draft.findCategory;
    var _a = react_1.useState(personas_mock_data_1.PERSONAS_MOCK_SCHEMA[0] ? personas_mock_data_1.PERSONAS_MOCK_SCHEMA[0].id : null), selectedCategoryId = _a[0], setSelectedCategoryId = _a[1];
    var _b = react_1.useState([]), selectedPropertyIds = _b[0], setSelectedPropertyIds = _b[1];
    var _c = react_1.useState(null), categoryEdit = _c[0], setCategoryEdit = _c[1];
    var _d = react_1.useState(''), successMessage = _d[0], setSuccessMessage = _d[1];
    var _e = react_1.useState(null), openUseCasesPropertyId = _e[0], setOpenUseCasesPropertyId = _e[1];
    var _f = react_1.useState(false), isDeleteDialogOpen = _f[0], setIsDeleteDialogOpen = _f[1];
    var _g = react_1.useState(''), searchText = _g[0], setSearchText = _g[1];
    var selectedCategory = react_1.useMemo(function () { return findCategory(selectedCategoryId); }, [findCategory, selectedCategoryId]);
    var search = use_personas_search_1.usePersonasSearch(draftSchema, searchText);
    var visibleSelectedProperties = react_1.useMemo(function () { return search.visiblePropertiesFor(selectedCategory); }, [search, selectedCategory]);
    var allVisiblePropertyIds = react_1.useMemo(function () { return visibleSelectedProperties.map(function (property) { return property.id; }); }, [visibleSelectedProperties]);
    var visibleSelectedCount = react_1.useMemo(function () {
        return selectedPropertyIds.filter(function (propertyId) { return allVisiblePropertyIds.indexOf(propertyId) >= 0; })
            .length;
    }, [selectedPropertyIds, allVisiblePropertyIds]);
    var moveToOptions = react_1.useMemo(function () {
        return draftSchema.map(function (category) { return ({
            key: category.id,
            text: category.name + " (" + category.properties.length + ")",
            disabled: category.id === selectedCategoryId,
        }); });
    }, [draftSchema, selectedCategoryId]);
    // --- Handlers --------------------------------------------------------
    var dismissSuccessOnEdit = function () {
        if (successMessage) {
            setSuccessMessage('');
        }
    };
    var handleSelectCategory = function (category) {
        setSelectedCategoryId(category.id);
        setSelectedPropertyIds([]);
        if (categoryEdit && categoryEdit.mode === 'edit' && categoryEdit.categoryId !== category.id) {
            setCategoryEdit(null);
        }
    };
    var handleStartAddCategory = function () {
        setCategoryEdit({ mode: 'add', categoryId: null, draftName: '' });
    };
    var handleStartEditCategory = function () {
        if (!selectedCategory) {
            return;
        }
        setCategoryEdit({
            mode: 'edit',
            categoryId: selectedCategory.id,
            draftName: selectedCategory.name,
        });
    };
    var handleConfirmCategoryEdit = function () {
        if (!categoryEdit) {
            return;
        }
        var nextName = categoryEdit.draftName.trim();
        if (!nextName) {
            return;
        }
        if (categoryEdit.mode === 'add') {
            var newId = personas_utils_1.generatePersonasCategoryId(draftSchema);
            var newCategory_1 = {
                id: newId,
                name: nextName,
                kind: 'predefined',
                properties: [],
            };
            updateDraft(function (next) { return __spreadArrays([newCategory_1], next); });
            setSelectedCategoryId(newId);
            setSelectedPropertyIds([]);
        }
        else if (categoryEdit.categoryId) {
            var categoryId_1 = categoryEdit.categoryId;
            // Once an AI-generated group has been edited by an admin, treat it
            // as a curated/predefined group so the badge no longer claims the
            // name was machine-generated.
            updateDraft(function (next) {
                return next.map(function (category) {
                    return category.id === categoryId_1
                        ? __assign(__assign({}, category), { name: nextName, kind: 'predefined' }) : category;
                });
            });
        }
        setCategoryEdit(null);
        dismissSuccessOnEdit();
    };
    var handleDiscardCategoryEdit = function () {
        setCategoryEdit(null);
    };
    var handleConfirmDeleteCategory = function () {
        if (!selectedCategoryId) {
            setIsDeleteDialogOpen(false);
            return;
        }
        var deletedId = selectedCategoryId;
        updateDraft(function (next) { return next.filter(function (category) { return category.id !== deletedId; }); });
        setSelectedPropertyIds([]);
        setCategoryEdit(null);
        var remaining = draftSchema.filter(function (category) { return category.id !== deletedId; });
        setSelectedCategoryId(remaining[0] ? remaining[0].id : null);
        setIsDeleteDialogOpen(false);
        dismissSuccessOnEdit();
    };
    var handleTogglePropertySelection = function (propertyId) {
        setSelectedPropertyIds(function (current) {
            return current.indexOf(propertyId) >= 0
                ? current.filter(function (id) { return id !== propertyId; })
                : current.concat(propertyId);
        });
    };
    var handleToggleSelectAll = function (nextChecked) {
        if (nextChecked) {
            setSelectedPropertyIds(allVisiblePropertyIds.slice());
        }
        else {
            setSelectedPropertyIds(function (current) {
                return current.filter(function (propertyId) { return allVisiblePropertyIds.indexOf(propertyId) < 0; });
            });
        }
    };
    var handleMoveSelected = function (targetCategoryId) {
        if (!selectedCategoryId || selectedPropertyIds.length === 0) {
            return;
        }
        var sourceCategoryId = selectedCategoryId;
        updateDraft(function (next) {
            var source = next.find(function (c) { return c.id === sourceCategoryId; });
            var target = next.find(function (c) { return c.id === targetCategoryId; });
            if (!source || !target) {
                return next;
            }
            var moved = source.properties.filter(function (property) { return selectedPropertyIds.indexOf(property.id) >= 0; });
            source.properties = source.properties.filter(function (property) { return selectedPropertyIds.indexOf(property.id) < 0; });
            target.properties = target.properties.concat(moved);
            return next;
        });
        setSelectedPropertyIds([]);
        dismissSuccessOnEdit();
    };
    var handleSaveChanges = function () {
        saveChanges();
        setCategoryEdit(null);
        setSuccessMessage('Persona groups saved successfully.');
    };
    var handleDiscardChanges = function () {
        discardChanges();
        setCategoryEdit(null);
        setSelectedPropertyIds([]);
        setSuccessMessage('');
        if (!savedSchema.some(function (category) { return category.id === selectedCategoryId; })) {
            setSelectedCategoryId(savedSchema[0] ? savedSchema[0].id : null);
        }
    };
    var handleOpenUseCase = function (useCaseId) {
        history.push(ai_hub_route_utils_1.buildAiHubUseCaseDetailsPath(useCaseId));
    };
    var handleToggleUseCases = function (propertyId) {
        setOpenUseCasesPropertyId(function (current) { return (current === propertyId ? null : propertyId); });
    };
    // --- Derived UI state ------------------------------------------------
    var isEditingCategory = categoryEdit !== null;
    var canEditCategory = !!selectedCategory && !isEditingCategory;
    var canDeleteCategory = !!selectedCategory && !isEditingCategory && selectedCategory.properties.length === 0;
    var selectAllChecked = allVisiblePropertyIds.length > 0 && visibleSelectedCount === allVisiblePropertyIds.length;
    var selectAllIndeterminate = visibleSelectedCount > 0 && visibleSelectedCount < allVisiblePropertyIds.length;
    var deleteTooltip = !selectedCategory
        ? 'Select an empty persona group to delete it'
        : selectedCategory.properties.length > 0
            ? 'Only persona groups without any personas can be deleted. Move or remove the personas first.'
            : 'Delete selected persona group';
    var matchedSignalCountFor = function (categoryId) {
        if (!search.isSearching) {
            return 0;
        }
        var entry = search.analysisByCategory[categoryId];
        return entry ? entry.matchingProperties.length : 0;
    };
    return (react_1.default.createElement("div", { className: "ws-ai-hub-admin-personas" },
        react_1.default.createElement(PersonasSaveControls_1.PersonasSaveControls, { title: "Personas", description: constants_1.PERSONAS_PAGE_DESCRIPTION, hasChanges: hasChanges, onSave: handleSaveChanges, onDiscard: handleDiscardChanges }),
        react_1.default.createElement("div", { className: "ws-personas-category-toolbar-row" },
            react_1.default.createElement("div", { className: "ws-personas-category-toolbar-row-actions" },
                react_1.default.createElement(PersonasToolbar_1.PersonasToolbar, { edit: categoryEdit, canEdit: canEditCategory, canDelete: canDeleteCategory, deleteTooltip: deleteTooltip, onStartAdd: handleStartAddCategory, onStartEdit: handleStartEditCategory, onRequestDelete: function () { return setIsDeleteDialogOpen(true); }, onChangeDraftName: function (nextValue) {
                        return setCategoryEdit(function (current) {
                            return current ? __assign(__assign({}, current), { draftName: nextValue }) : current;
                        });
                    }, onConfirmEdit: handleConfirmCategoryEdit, onDiscardEdit: handleDiscardCategoryEdit })),
            react_1.default.createElement("div", { className: "ws-personas-category-toolbar-row-search" },
                react_1.default.createElement(office_ui_fabric_react_1.SearchBox, { placeholder: "Search persona groups and personas", value: searchText, disableAnimation: true, onChange: function (_, nextValue) { return setSearchText(nextValue || ''); }, onClear: function () { return setSearchText(''); }, styles: { root: { minWidth: 240, maxWidth: 320 } } }))),
        successMessage && (react_1.default.createElement(office_ui_fabric_react_1.MessageBar, { messageBarType: office_ui_fabric_react_1.MessageBarType.success, onDismiss: function () { return setSuccessMessage(''); }, dismissButtonAriaLabel: "Close" }, successMessage)),
        react_1.default.createElement("div", { className: "ws-personas-body" },
            react_1.default.createElement(PersonasCategoriesPanel_1.PersonasCategoriesPanel, { draftSchema: draftSchema, visibleCategories: search.visibleCategories, hasResults: search.hasResults, searchText: searchText, searchMatcher: search.searchMatcher, selectedCategoryId: selectedCategoryId, isCategoryChanged: isCategoryChanged, matchedSignalCountFor: matchedSignalCountFor, onSelectCategory: handleSelectCategory }),
            react_1.default.createElement(PersonasPropertiesPanel_1.PersonasPropertiesPanel, { selectedCategory: selectedCategory, visibleProperties: visibleSelectedProperties, isSearching: search.isSearching, searchText: searchText, searchMatcher: search.searchMatcher, selectedPropertyIds: selectedPropertyIds, selectAllChecked: selectAllChecked, selectAllIndeterminate: selectAllIndeterminate, visibleSelectedCount: visibleSelectedCount, moveToOptions: moveToOptions, openUseCasesPropertyId: openUseCasesPropertyId, isPropertyChanged: isPropertyChanged, onToggleSelectAll: handleToggleSelectAll, onMoveSelected: handleMoveSelected, onTogglePropertySelection: handleTogglePropertySelection, onToggleUseCases: handleToggleUseCases, onDismissUseCases: function () { return setOpenUseCasesPropertyId(null); }, onOpenUseCase: handleOpenUseCase })),
        react_1.default.createElement(components_1.ConfirmDialog, { hidden: !isDeleteDialogOpen, variant: "error", title: "Delete persona group", message: selectedCategory
                ? "Are you sure you want to delete \"" + (selectedCategory.name || constants_1.PERSONAS_UNTITLED_GROUP) + "\"? This change will apply once you click Save Changes."
                : 'Are you sure you want to delete this persona group?', confirmText: "Delete", cancelText: "Cancel", onConfirm: handleConfirmDeleteCategory, onCancel: function () { return setIsDeleteDialogOpen(false); } })));
};
exports.WsAiHubAdminPersonas = WsAiHubAdminPersonas;
